import { axiosInstance } from "@/config/axios";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";
import { objectToCamel, objectToSnake } from "ts-case-convert";
import { Commodity, Process } from "@/types/contract";
import { getMappedCustomerType } from "@/utils/customer";
import { ContractConfig } from "@/types/contractConfig";
import { getPreviousConnectionFromString } from "@/utils/supplyPoint";
import { values } from "remeda";

const responseSchema = z.object({
  contract: z.object({
    contractUuid: z.string().uuid(),
    sessionKey: z.string().uuid(),
  }),
});

export const createContract = async (values: ContractConfig) => {
  const response = await axiosInstance.post("/contract", objectToSnake(createContractBody(values)));
  const parsedResponse = responseSchema.safeParse(objectToCamel(response.data));
  if (!parsedResponse.success) {
    console.error(parsedResponse.error.issues);
    throw new Error("zod error");
  }
  return parsedResponse.data.contract;
};

const createContractBody = ({
  productId,
  process,
  commodity,
  contractSource,
  yearConsumption,
  yearConsumptionUnits,
  yearConsumptionVt,
  yearConsumptionVtUnits,
  yearConsumptionNt,
  yearConsumptionNtUnits,
  region,
  district,
  tariff,
  ecoType,
  greenProduct,
  tdS01,
  tdS02,
  tdS03,
  tdS04,
  supplyPointUsage,
  customerType,
  connection,
  pds,
  authorId,
  contractMethod,
  sellChannel,
  sellChannelType,
  sop,
  segment,
  segmentServiceClass,
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
  utmId,
  utmCmpid,
  sessionId,
  gclid,
  fbclid,
  bingclid,
  msclkid,
  sznclid,
  contractNote,
}: ContractConfig) => {
  const preselectGreenProduct = process === Process.NEW_POINT && commodity === Commodity.Electricity;

  return {
    contract: {
      authorId,
      selfService: true,
      agreements: [
        {
          agreement_id: "AGGRWEBAGGR_WEB_MGDPR01",
          agreement_type: "GDPR01",
          agreed: false,
        },
        {
          agreement_id: "AGGRWEBAGGR_WEB_MEOPM",
          agreement_type: "EOPM",
          agreed: true,
        },
      ],
      contractDetails: {
        productId,
        contractMethod,
      },
      process,
      customer: getMappedCustomerType(customerType),
      uuid: uuidv4(),
      segment,
      segmentServiceClass,
      supplyPoints: [
        {
          commodity,
          yearConsumption,
          yearConsumptionUnits,
          yearConsumptionVt,
          yearConsumptionVtUnits,
          yearConsumptionNt,
          yearConsumptionNtUnits,
          tariff,
          region,
          district,
          sop: {
            self: sop,
            supplyPointUsage,
            tdS01,
            tdS02,
            tdS03,
            tdS04,
          },
          forbiddenOverflow: productId?.includes("_P_") ?? false,
          previousConnection: getPreviousConnectionFromString(connection),
          distributionArea: pds,
        },
      ],
      contractUtms: parseUTMParams({
        source: contractSource,
        utmSource,
        utmMedium,
        utmCampaign,
        utmContent,
        utmTerm,
        utmId,
        utmCmpid,
        sessionId,
        gclid,
        fbclid,
        bingclid,
        msclkid,
        sznclid,
      }),
      contractSource,
      ecoType: preselectGreenProduct ? "M" : ecoType,
      greenProduct: preselectGreenProduct ? true : greenProduct,
      sellChannel,
      sellChannelType,
      contractNote,
    },
  };
};

// removes null values, creates utms array or returns undefined if all values are null
export const parseUTMParams = (utmsParams: Record<string, string | null>) => {
  const contractUtms = Object.fromEntries(Object.entries(utmsParams).filter(([_, v]) => v != null));
  return values(contractUtms).length > 0 ? [contractUtms] : undefined;
};
